import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { Menu } from '../models/menu';
import { FNFResponse } from '../models/response';
import { FeatureFlag } from '../utils/feature-flag.util';
import {
  DateExceptionKind,
  HourType,
  HoursRange,
  ServingHoursDoc,
  ServingHoursGroup,
  withGroupsOfType,
} from '../utils/serving-hours.util';
import { HoursStatus } from '../utils/open-status.util';
import { ServiceConstant } from '../utils/service-constant.util';
import { isPosManaged, POS_PROVIDER } from '../utils/pos-provider.util';

@Injectable({
  providedIn: 'root',
})
export class RestaurantService {
  httpClient = inject(HttpClient);
  updateRestaurantLogo$ = new BehaviorSubject<any>(null);
  currentRestaurantDetails$ = new BehaviorSubject<any>(null);
  restaurantPosProvider$ = new BehaviorSubject<any>('fnf');
  restaurantFeatureFlags$ = new BehaviorSubject<any>(null);
  restaurantSubscription$ = new BehaviorSubject<any>(null);
  restaurantPaymentMethod$ = new BehaviorSubject<any>(null);
  /** Resolved name (e.g. 'Free' / 'Standard' / 'Premium') of the restaurant's
   * current subscription plan — populated once HomePageComponent resolves
   * restaurantSubscription$.planId against the plan catalog. */
  currentPlanName$ = new BehaviorSubject<string | null>(null);

  /**
   * Fires when something outside the sidebar learns the restaurant changed on
   * the server — a `RESTAURANT_ACTIVATED` push, say — and the details behind
   * `currentRestaurantDetails$` should be fetched again. The sidebar owns that
   * fetch (it also drives the menu locks), so it listens here.
   */
  readonly restaurantDetailsRefresh$ = new Subject<void>();

  /** Asks whoever owns the restaurant-details fetch to run it again. */
  requestRestaurantDetailsRefresh(): void {
    this.restaurantDetailsRefresh$.next();
  }

  /**
   * Fires when the subscription changed on the server — a plan-change or
   * billing push (FNF-592). `restaurantSubscription$` / `currentPlanName$`
   * are refreshed by the caller; this tells an open Billing page to reload
   * its own view (invoices, payment method, next invoice).
   */
  readonly subscriptionRefresh$ = new Subject<void>();

  requestSubscriptionRefresh(): void {
    this.subscriptionRefresh$.next();
  }

  constructor() {}

  addImage(formData: FormData): Observable<any> {
    return this.httpClient
      .post<any>(ServiceConstant.apiUrl + '/commons/file/upload', formData)
      .pipe(
        catchError((error) => {
          console.error('Error add image:', error);
          return of(error);
        }),
      );
  }

  getRestaurantDetails(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/restaurants').pipe(
      catchError((error) => {
        console.error('Error fetching restaurant details:', error);
        return of(error);
      }),
    );
  }

  /**
   * "Enable Fork & Find" — shared by the Hardware page and the home-page
   * choose-POS prompt. Requests activation and, unless the catalogue is owned
   * by Square / Clover, picks Fork & Find as the POS (a `none` restaurant
   * becomes `fnf`). On success the cached restaurant and provider subjects are
   * updated so every subscriber reacts without a refetch. Errors come back the
   * same way `editRestaurantDetails` returns them (`response.error`).
   */
  enableForkAndFind(restaurant: any): Observable<any> {
    const switchToFnf = !isPosManaged(restaurant?.pos_provider);
    const payload: any = { request_activation: true };
    if (switchToFnf) {
      payload.pos_provider = POS_PROVIDER.FNF;
    }
    return this.editRestaurantDetails(payload, restaurant?.restaurant_id).pipe(
      tap((response: any) => {
        if (!response?.data && response?.error) {
          return;
        }
        const current = this.currentRestaurantDetails$.value ?? restaurant;
        if (!current) {
          return;
        }
        const updated = { ...current, request_activation: true };
        if (switchToFnf) {
          updated.pos_provider = POS_PROVIDER.FNF;
        }
        this.currentRestaurantDetails$.next(updated);
        this.restaurantPosProvider$.next(updated.pos_provider);
      }),
    );
  }

  editRestaurantDetails(restaurant: any, id: any): Observable<any> {
    return this.httpClient.patch<any>(ServiceConstant.apiUrl + '/restaurants', restaurant).pipe(
      catchError((error) => {
        console.error('Error editing restaurant details:', error);
        return of(error);
      }),
    );
  }

  getRestaurantTypeAndCategories(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/restaurants/types').pipe(
      catchError((error) => {
        console.error('Error fetching types:', error);
        return of(error);
      }),
    );
  }

  addRestaurantType(type: any): Observable<any> {
    return this.httpClient.post<any>(ServiceConstant.apiUrl + '/restaurants/types', type).pipe(
      catchError((error) => {
        console.error('Error fetching restaurant Type:', error);
        return of(error);
      }),
    );
  }

  editMenuType(type: any): Observable<any> {
    return this.httpClient
      .put<any>(ServiceConstant.apiUrl + '/restaurants/types/' + type.id, type)
      .pipe(
        catchError((error) => {
          console.error('Error editing menu Type:', error);
          return of(error);
        }),
      );
  }

  addTypeCategory(typeId: any, categories: any): Observable<any> {
    return this.httpClient
      .post<any>(
        ServiceConstant.apiUrl + '/restaurants/types/' + typeId + '/categories',
        categories,
      )
      .pipe(
        catchError((error) => {
          console.error('Error adding type categories:', error);
          return of(error);
        }),
      );
  }

  deleteMenuType(typeId: any): Observable<any> {
    return this.httpClient
      .delete<any>(ServiceConstant.apiUrl + '/restaurants/types/' + typeId)
      .pipe(
        catchError((error) => {
          console.error('Error deleting menu type:', error);
          return of(error);
        }),
      );
  }

  deleteTypeCategory(typeId: any, categoryId: any): Observable<any> {
    return this.httpClient
      .delete<any>(
        ServiceConstant.apiUrl + '/restaurants/types/' + typeId + '/categories/' + categoryId,
      )
      .pipe(
        catchError((error) => {
          console.error('Error deleting type category:', error);
          return of(error);
        }),
      );
  }

  addRestaurantZone(zone: any): Observable<any> {
    return this.httpClient.post<any>(ServiceConstant.apiUrl + '/restaurants/zones', zone).pipe(
      catchError((error) => {
        console.error('Error creating restaurant zone:', error);
        return of(error);
      }),
    );
  }

  editRestaurantZone(zone: any, id: any): Observable<any> {
    return this.httpClient.put<any>(ServiceConstant.apiUrl + '/restaurants/zones/' + id, zone).pipe(
      catchError((error) => {
        console.error('Error editing restaurant zone:', error);
        return of(error);
      }),
    );
  }

  /** Partial zone update (e.g. only `layout`); PUT requires the full zone. */
  patchRestaurantZone(zone: any, id: any): Observable<any> {
    return this.httpClient
      .patch<any>(ServiceConstant.apiUrl + '/restaurants/zones/' + id, zone)
      .pipe(
        catchError((error) => {
          console.error('Error patching restaurant zone:', error);
          return of(error);
        }),
      );
  }

  deleteZone(zoneIdId: any): Observable<any> {
    return this.httpClient
      .delete<any>(ServiceConstant.apiUrl + '/restaurants/zones/' + zoneIdId)
      .pipe(
        catchError((error) => {
          console.error('Error deleting zone:', error);
          return of(error);
        }),
      );
  }

  getRestaurantZones(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/restaurants/zones').pipe(
      catchError((error) => {
        console.error('Error fetching zones:', error);
        return of(error);
      }),
    );
  }

  getServingTime(): Observable<any> {
    return this.httpClient.get<Menu[]>(ServiceConstant.apiUrl + '/restaurants/serving_times').pipe(
      catchError((error) => {
        console.error('Error fetching all serving times:', error);
        return of(error);
      }),
    );
  }

  addServingTime(payload: any): Observable<any> {
    return this.httpClient
      .post<any>(ServiceConstant.apiUrl + '/restaurants/serving_times', payload)
      .pipe(
        catchError((error) => {
          console.error('Error fetching new serving time:', error);
          return of(error);
        }),
      );
  }

  editServingTime(time: any, id: any): Observable<any> {
    return this.httpClient
      .patch<any>(ServiceConstant.apiUrl + '/restaurants/serving_times/' + id, time)
      .pipe(
        catchError((error) => {
          console.error('Error editing restaurant serving time:', error);
          return of(error);
        }),
      );
  }

  deleteServingTime(timeId: any): Observable<any> {
    return this.httpClient
      .delete<any>(ServiceConstant.apiUrl + '/restaurants/serving_times/' + timeId)
      .pipe(
        catchError((error) => {
          console.error('Error deleting serving time:', error);
          return of(error);
        }),
      );
  }

  // Reservation methods
  getReservations(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/restaurants/reservations').pipe(
      catchError((error) => {
        console.error('Error fetching all reservations:', error);
        return of(error);
      }),
    );
  }

  addReservation(payload: any): Observable<any> {
    return this.httpClient
      .post<any>(ServiceConstant.apiUrl + '/restaurants/reservations', payload)
      .pipe(
        catchError((error) => {
          console.error('Error creating new reservation:', error);
          return of(error);
        }),
      );
  }

  editReservation(reservation: any, id: any): Observable<any> {
    return this.httpClient
      .patch<any>(ServiceConstant.apiUrl + '/restaurants/reservations/' + id, reservation)
      .pipe(
        catchError((error) => {
          console.error('Error editing reservation:', error);
          return of(error);
        }),
      );
  }

  deleteReservation(reservationId: any): Observable<any> {
    return this.httpClient
      .delete<any>(ServiceConstant.apiUrl + '/restaurants/reservations/' + reservationId)
      .pipe(
        catchError((error) => {
          console.error('Error deleting reservation:', error);
          return of(error);
        }),
      );
  }

  getAllRestaurants(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/restaurants/all').pipe(
      catchError((error) => {
        console.error('Error fetching all restaurants list:', error);
        return of(error);
      }),
    );
  }

  clearRestaurantCache(): Observable<any> {
    return this.httpClient.delete<any>(ServiceConstant.apiUrl + '/restaurants/cache/clear').pipe(
      catchError((error) => {
        console.error('Error clearing restaurant cache:', error);
        return of(error);
      }),
    );
  }

  clearMenuCache(): Observable<any> {
    return this.httpClient
      .delete<any>(ServiceConstant.apiUrl + '/restaurants/menus/cache/clear')
      .pipe(
        catchError((error) => {
          console.error('Error clearing menu cache:', error);
          return of(error);
        }),
      );
  }

  /**
   * Closes a date, or gives it its own hours (FNF-1179). Replaces whatever was
   * set for that date. Returns the whole hours document; throws on failure.
   */
  setDateException(
    serviceDate: string,
    body: { kind: DateExceptionKind; hours: HoursRange[]; note: string | null },
  ): Observable<ServingHoursDoc> {
    return this.httpClient
      .put<FNFResponse<ServingHoursDoc>>(
        ServiceConstant.apiUrl + '/restaurants/hours/exceptions/' + serviceDate,
        body,
      )
      .pipe(map((res) => this.hoursDocOrThrow(res)));
  }

  /** Back to the weekly hours on that date. */
  removeDateException(serviceDate: string): Observable<ServingHoursDoc> {
    return this.httpClient
      .delete<FNFResponse<ServingHoursDoc>>(
        ServiceConstant.apiUrl + '/restaurants/hours/exceptions/' + serviceDate,
      )
      .pipe(map((res) => this.hoursDocOrThrow(res)));
  }

  /**
   * Open right now, why, and the temporary closure while one is in force
   * (FNF-1612). The header chip and the Restaurant Status card both read it,
   * so closing on one updates the other.
   */
  readonly hoursStatus$ = new BehaviorSubject<HoursStatus | null>(null);

  /** Reads the status again; null (and no chip) when it can't be read. */
  refreshHoursStatus(): Observable<HoursStatus | null> {
    return this.httpClient
      .get<FNFResponse<HoursStatus>>(ServiceConstant.apiUrl + '/restaurants/hours/status')
      .pipe(
        map((res) => res?.data ?? null),
        catchError(() => of(null)),
        tap((status) => this.hoursStatus$.next(status)),
      );
  }

  /**
   * Closes the whole restaurant now: no orders, bookings or walk-ins until
   * `until` (UTC), or until reopened when null. Throws on failure.
   */
  setTemporaryClosure(body: {
    until: string | null;
    reason: string | null;
  }): Observable<HoursStatus> {
    return this.httpClient
      .put<FNFResponse<HoursStatus>>(ServiceConstant.apiUrl + '/restaurants/hours/closure', body)
      .pipe(
        map((res) => this.statusOrThrow(res)),
        tap((status) => this.hoursStatus$.next(status)),
      );
  }

  /** Reopens now. Fine to call with nothing closed. Throws on failure. */
  clearTemporaryClosure(): Observable<HoursStatus> {
    return this.httpClient
      .delete<FNFResponse<HoursStatus>>(ServiceConstant.apiUrl + '/restaurants/hours/closure')
      .pipe(
        map((res) => this.statusOrThrow(res)),
        tap((status) => this.hoursStatus$.next(status)),
      );
  }

  private statusOrThrow(res: FNFResponse<HoursStatus> | null): HoursStatus {
    if (!res?.data) {
      throw new Error(res?.error?.message || 'The restaurant status was not saved.');
    }
    return res.data;
  }

  private hoursDocOrThrow(res: FNFResponse<ServingHoursDoc> | null): ServingHoursDoc {
    if (!res?.data) {
      throw new Error(res?.error?.message || 'The hours were not saved.');
    }
    return res.data;
  }

  /**
   * Saves one kind of serving hours (opening or reservation hours) and keeps
   * the other kinds as they are. The document is read again first, so a save
   * from another page in the meantime isn't overwritten. Unlike the other
   * calls here, errors are thrown, so callers can tell a failed save apart.
   */
  saveServingHoursOfType(type: HourType, groups: ServingHoursGroup[]): Observable<ServingHoursDoc> {
    const url = ServiceConstant.apiUrl + '/restaurants/hours';
    return this.httpClient.get<FNFResponse<ServingHoursDoc | null>>(url).pipe(
      switchMap((res) => {
        const current = res?.data ?? null;
        const serving_hours = withGroupsOfType(current?.serving_hours, type, groups);
        if (!current?.serving_hour_id) {
          if (!serving_hours.length) {
            // Nothing saved yet and nothing to save.
            return of({ data: current ?? {} } as FNFResponse<ServingHoursDoc>);
          }
          return this.httpClient.post<FNFResponse<ServingHoursDoc>>(url, { serving_hours });
        }
        return this.httpClient.put<FNFResponse<ServingHoursDoc>>(
          url + '/' + current.serving_hour_id,
          { serving_hours },
        );
      }),
      map((res) => this.hoursDocOrThrow(res)),
    );
  }

  getRestaurantServingHours(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/restaurants/hours').pipe(
      catchError((error) => {
        console.error('Error fetching restaurant serving hours:', error);
        return of(error);
      }),
    );
  }

  getRestaurantFeatureFlags(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/admin/feature-flags/minimal').pipe(
      catchError((error) => {
        console.error('Error fetching restaurant feature flags:', error);
        return of(error);
      }),
    );
  }

  /**
   * The published flags, fetching and publishing them first when nothing has
   * loaded yet — route guards run before HomePageComponent has fetched them.
   * Emits null when the flags can't be loaded.
   */
  loadFeatureFlags(): Observable<FeatureFlag[] | null> {
    const loaded = this.restaurantFeatureFlags$.value;
    if (loaded) {
      return of(loaded);
    }
    return this.getRestaurantFeatureFlags().pipe(
      map((response: FNFResponse<any>) => {
        const flags: FeatureFlag[] | null = response?.data?.flags ?? null;
        if (flags) {
          this.restaurantFeatureFlags$.next(flags);
        }
        return flags;
      }),
    );
  }

  updateAgreementDocument(): Observable<any> {
    return this.httpClient
      .patch<any>(ServiceConstant.apiUrl + '/restaurants/is-agreement-signed', {
        is_agreement_signed: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error editing restaurant details:', error);
          return of(error);
        }),
      );
  }
}
